import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import { DEFAULTS, SOURCES, assess, hasResultParams, inputsFromSearch, sanitize, searchFromInputs } from './migrationAssessment';
import type { AssessmentInputs, Level, SourceKey } from './migrationAssessment';

const SITE_URL = 'https://www.greyfielddata.com';
const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

const PAGE: Record<SourceKey, { path: string; title: string; seo: string; description: string; translator: string; service: string; plan: string }> = {
  tableau: {
    path: '/tools/tableau-to-sigma-migration-assessment',
    title: 'Tableau to Sigma migration assessment',
    seo: 'Tableau to Sigma migration assessment: free readout',
    description: 'Describe your Tableau estate and get a readout: how big the move to Sigma is, what needs rebuilding, a pilot pick and phase plan, and what to prepare.',
    translator: '/tools/tableau-to-sigma-formula-translator',
    service: '/services/tableau-to-sigma',
    plan: '/blog/how-to-plan-a-tableau-to-sigma-migration',
  },
  powerbi: {
    path: '/tools/power-bi-to-sigma-migration-assessment',
    title: 'Power BI to Sigma migration assessment',
    seo: 'Power BI to Sigma migration assessment: free readout',
    description: 'Describe your Power BI estate and get a readout: how big the move to Sigma is, what needs rebuilding, a pilot pick and phase plan, and what to prepare.',
    translator: '/tools/dax-to-sigma-formula-translator',
    service: '/services/power-bi-to-sigma',
    plan: '/blog/how-to-plan-a-power-bi-to-sigma-migration',
  },
};

const LEVEL_STYLE: Record<Level, string> = {
  Rebuild: 'bg-[#ffe8c7] text-[#7a3d00]',
  Check: 'bg-[#ece4fb] text-[#4a2a91]',
  Direct: 'bg-[#dbe8fb] text-[#0f3f80]',
};

const WAREHOUSE_LABELS: Record<string, string> = {
  snowflake: 'Snowflake', bigquery: 'BigQuery', databricks: 'Databricks', redshift: 'Amazon Redshift', other: 'Another cloud warehouse', none: 'No cloud warehouse yet',
};

type NumberKey = 'reports' | 'users' | 'sources' | 'extracts' | 'calcs' | 'complex' | 'window' | 'params';

function NumberField({ id, label, help, value, onChange }: { id: string; label: string; help: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-slate-900">{label}</label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={`${id}-help`}
        className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-300 px-3 text-slate-950 outline-none focus:border-[#1a4e94] focus:ring-2 focus:ring-blue-100"
      />
      <p id={`${id}-help`} className="mt-1.5 text-xs leading-5 text-slate-500">{help}</p>
    </div>
  );
}

export default function MigrationAssessmentPage({ source }: { source: SourceKey }) {
  const meta = SOURCES[source];
  const page = PAGE[source];
  const [draft, setDraft] = React.useState<Record<NumberKey, string>>(() => ({
    reports: String(DEFAULTS.reports), users: String(DEFAULTS.users), sources: String(DEFAULTS.sources), extracts: String(DEFAULTS.extracts),
    calcs: String(DEFAULTS.calcs), complex: String(DEFAULTS.complex), window: String(DEFAULTS.window), params: String(DEFAULTS.params),
  }));
  const [warehouse, setWarehouse] = React.useState<string>(DEFAULTS.warehouse);
  const [usageKnown, setUsageKnown] = React.useState<'yes' | 'no'>('no');
  const [features, setFeatures] = React.useState<string[]>([]);
  const [step, setStep] = React.useState(0);
  const [showReadout, setShowReadout] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const readoutRef = React.useRef<HTMLDivElement>(null);

  const raw = { ...draft, warehouse, usageKnown, features };
  const result = assess(raw, source);

  function load(i: AssessmentInputs) {
    setDraft({
      reports: String(i.reports), users: String(i.users), sources: String(i.sources), extracts: String(i.extracts),
      calcs: String(i.calcs), complex: String(i.complex), window: String(i.window), params: String(i.params),
    });
    setWarehouse(i.warehouse);
    setUsageKnown(i.usageKnown);
    setFeatures(i.features);
  }

  React.useEffect(() => {
    if (hasResultParams(window.location.search)) {
      load(inputsFromSearch(window.location.search, source));
      setShowReadout(true);
    }
  }, [source]);

  function writeUrl() {
    const params = new URLSearchParams(window.location.search);
    new URLSearchParams(searchFromInputs(raw, source)).forEach((v, k) => params.set(k, v));
    if (!features.length) params.delete('features');
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
  }

  function seeReadout() {
    writeUrl();
    setShowReadout(true);
    window.setTimeout(() => { readoutRef.current?.focus({ preventScroll: true }); readoutRef.current?.scrollIntoView({ block: 'start' }); }, 0);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}${page.path}${searchFromInputs(raw, source)}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const set = (key: NumberKey) => (value: string) => setDraft((d) => ({ ...d, [key]: value }));
  const toggle = (key: string) => setFeatures((f) => (f.includes(key) ? f.filter((x) => x !== key) : [...f, key]));
  const steps = ['Your estate', 'Calculations', 'Features in use'];
  const clean = sanitize(raw, source);

  return (
    <BlogLayout wide>
      <SEO title={page.seo} description={page.description} url={`${SITE_URL}${page.path}`} />
      <BreadcrumbStructuredData items={[{ name: 'Home', url: SITE_URL }, { name: 'Resources', url: `${SITE_URL}/resources` }, { name: page.title, url: `${SITE_URL}${page.path}` }]} />
      <style>{'@media print { body * { visibility: hidden; } #migration-readout, #migration-readout * { visibility: visible; } #migration-readout { position: absolute; inset: 0 auto auto 0; width: 100%; } .no-print { display: none !important; } }'}</style>

      <div className="no-print">
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
          <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
          <span aria-hidden="true" className="mx-2">/</span>
          <span className="text-slate-700">{meta.label} to Sigma</span>
        </nav>
        <header className="mt-5 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1a4e94]">Free tool · {meta.label} to Sigma</p>
          <h1 className="mt-3 text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">{page.title}</h1>
          <p className="mt-5 text-lg leading-8 text-slate-600">Describe your {meta.label} estate in a few minutes. Get a readout: how big the move is, what will need rebuilding, where to start, and what to prepare.</p>
          <p className="mt-3 text-sm text-slate-500">Runs in your browser. Nothing you enter is sent anywhere.</p>
        </header>
      </div>

      {!showReadout && (
        <div className="no-print mt-10 flex flex-wrap items-start gap-6">
          <form className="min-w-0 flex-[999_1_560px] space-y-5" onSubmit={(e) => { e.preventDefault(); if (step < 2) setStep(step + 1); else seeReadout(); }}>
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <span className="font-semibold text-slate-950">Step {step + 1} of 3 · {steps[step]}</span>
              <div className="h-1.5 flex-1 rounded-full bg-slate-200" aria-hidden="true">
                <div className="h-1.5 rounded-full bg-[#1a4e94] transition-all" style={{ width: `${((step + 1) / 3) * 100}%` }} />
              </div>
            </div>

            {step === 0 && (
              <fieldset className="rounded-3xl border border-slate-200 p-5 sm:p-7">
                <legend className="px-2 text-lg font-semibold text-slate-950">Your estate</legend>
                <div className="grid gap-6 sm:grid-cols-2">
                  <NumberField id="ma-reports" label={`${meta.reportNoun[0].toUpperCase()}${meta.reportNoun.slice(1)} you plan to move`} help="Count only what people still use, if you know." value={draft.reports} onChange={set('reports')} />
                  <NumberField id="ma-users" label="People who view them" help="Viewers and builders together." value={draft.users} onChange={set('users')} />
                  <NumberField id="ma-sources" label="Data sources" help={source === 'tableau' ? 'Published and embedded data sources.' : 'Semantic models (datasets).'} value={draft.sources} onChange={set('sources')} />
                  <NumberField id="ma-extracts" label={meta.extractLabel} help={source === 'tableau' ? 'Sources that use extracts rather than a live connection.' : 'Models that import data rather than use DirectQuery.'} value={draft.extracts} onChange={set('extracts')} />
                  <div>
                    <label htmlFor="ma-warehouse" className="block text-sm font-semibold text-slate-900">Where the data lives</label>
                    <select id="ma-warehouse" value={warehouse} onChange={(e) => setWarehouse(e.target.value)} className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 text-slate-950">
                      {Object.entries(WAREHOUSE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </div>
                  <fieldset>
                    <legend className="text-sm font-semibold text-slate-900">Do you know which ones people use?</legend>
                    <div className="mt-2 flex gap-3">
                      {(['yes', 'no'] as const).map((v) => (
                        <label key={v} className={`inline-flex min-h-[44px] cursor-pointer items-center rounded-xl border px-4 text-sm font-medium ${usageKnown === v ? 'border-[#1a4e94] bg-[#eaf2fd] text-[#0f3f80]' : 'border-slate-300'}`}>
                          <input type="radio" name="usage" className="mr-2" checked={usageKnown === v} onChange={() => setUsageKnown(v)} />
                          {v === 'yes' ? 'Yes, we have usage data' : 'Not yet'}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              </fieldset>
            )}

            {step === 1 && (
              <fieldset className="rounded-3xl border border-slate-200 p-5 sm:p-7">
                <legend className="px-2 text-lg font-semibold text-slate-950">Calculations</legend>
                <p className="mb-5 text-sm text-slate-600">Rough counts are fine. They change the size and the risk list, not the plan.</p>
                <div className="grid gap-6 sm:grid-cols-2">
                  <NumberField id="ma-calcs" label={meta.calcLabel} help="Across what you plan to move." value={draft.calcs} onChange={set('calcs')} />
                  <NumberField id="ma-complex" label={meta.complexLabel} help={meta.complexHelp} value={draft.complex} onChange={set('complex')} />
                  <NumberField id="ma-window" label={meta.windowLabel} help={meta.windowHelp} value={draft.window} onChange={set('window')} />
                  <NumberField id="ma-params" label={meta.paramLabel} help="User-chosen values that change what a view shows." value={draft.params} onChange={set('params')} />
                </div>
              </fieldset>
            )}

            {step === 2 && (
              <fieldset className="rounded-3xl border border-slate-200 p-5 sm:p-7">
                <legend className="px-2 text-lg font-semibold text-slate-950">Features in use</legend>
                <p className="mb-4 text-sm text-slate-600">Tick everything your {meta.reportNoun} rely on.</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {meta.features.map((f) => (
                    <label key={f.key} className={`flex min-h-[48px] cursor-pointer items-center rounded-xl border px-4 text-sm ${features.includes(f.key) ? 'border-[#1a4e94] bg-[#eaf2fd] font-semibold text-[#0f3f80]' : 'border-slate-300 text-slate-900'}`}>
                      <input type="checkbox" className="mr-3 h-4 w-4" checked={features.includes(f.key)} onChange={() => toggle(f.key)} />
                      {f.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="flex justify-between gap-3">
              <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} className="min-h-[44px] rounded-xl border border-slate-300 px-5 text-sm font-semibold text-slate-900 disabled:opacity-40">Back</button>
              <button type="submit" className="min-h-[44px] rounded-xl bg-[#0a1628] px-6 text-sm font-semibold text-white hover:bg-[#10203a]">
                {step < 2 ? `Next: ${steps[step + 1].toLowerCase()}` : 'See my readout'}
              </button>
            </div>
          </form>

          <aside className="min-w-0 flex-[1_1_320px] rounded-3xl border border-[#d6e3f5] bg-[#f3f7fd] p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#1a4e94]">Your readout will include</p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700">
              <li>The relative size of the move and why</li>
              <li>What needs rebuilding, not just copying</li>
              <li>A pilot pick and a phase plan</li>
              <li>What to prepare before anyone quotes it</li>
            </ul>
            <p className="mt-4 text-sm text-slate-600">Rather talk it through? <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#1a4e94] underline underline-offset-4">Book a free migration analysis</a>.</p>
          </aside>
        </div>
      )}

      {showReadout && (
        <div className="mt-10">
          <div className="no-print mb-4 flex flex-wrap gap-3">
            <button type="button" onClick={() => { setShowReadout(false); setStep(0); }} className="min-h-[44px] rounded-xl border border-slate-300 px-4 text-sm font-semibold">Edit answers</button>
            <button type="button" onClick={() => window.print()} className="min-h-[44px] rounded-xl border border-slate-300 px-4 text-sm font-semibold">Download as PDF</button>
            <button type="button" onClick={copyLink} className="min-h-[44px] rounded-xl border border-slate-300 px-4 text-sm font-semibold">{copied ? 'Link copied' : 'Copy a link to this readout'}</button>
          </div>

          <article id="migration-readout" ref={readoutRef} tabIndex={-1} aria-labelledby="readout-title" className="scroll-mt-32 overflow-hidden rounded-3xl border border-slate-200 bg-white outline-none">
            <header className="flex flex-wrap items-end justify-between gap-6 bg-[#0a1628] p-7 text-white sm:p-9" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
              <div className="min-w-0 flex-[1_1_420px]">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#93b4e6]">Migration readout · {meta.label} to Sigma</p>
                <h2 id="readout-title" className="mt-2 text-3xl font-semibold leading-tight">{result.headline}</h2>
                <p className="mt-2 text-sm text-slate-300">Prepared from your answers by greyfielddata.com. An estimate, not a quote.</p>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.08em] text-[#93b4e6]">Relative size</div>
                <div className="mt-1 text-4xl font-semibold">{result.size}</div>
                <div className="mt-2 flex justify-end gap-1" aria-hidden="true">
                  {[1, 2, 3].map((n) => <span key={n} className={`h-1.5 w-7 rounded ${n <= result.sizeLevel ? 'bg-[#93b4e6]' : 'bg-[#334e78]'}`} />)}
                </div>
              </div>
            </header>

            <dl className="grid grid-cols-2 gap-4 border-b border-slate-200 p-7 sm:grid-cols-4 sm:p-9">
              {[
                [`${meta.reportNoun[0].toUpperCase()}${meta.reportNoun.slice(1)} to move`, clean.reports.toLocaleString('en-US')],
                ['Viewers', clean.users.toLocaleString('en-US')],
                ['Data sources', clean.sources.toLocaleString('en-US')],
                ['Suggested phases', String(result.phases.length)],
              ].map(([label, value]) => (
                <div key={label}><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-1 text-2xl font-semibold text-[#0a1628]">{value}</dd></div>
              ))}
            </dl>

            <section className="border-b border-slate-200 p-7 sm:p-9">
              <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-[#1a4e94]">What drives the size</h3>
              <ul className="mt-4 space-y-3">
                {result.risks.map((risk) => (
                  <li key={risk.title} className="grid gap-3 rounded-2xl border border-slate-200 p-4 sm:grid-cols-[110px_minmax(0,1fr)]">
                    <span className={`self-start justify-self-start rounded-full px-2.5 py-1 text-xs font-semibold ${LEVEL_STYLE[risk.level]}`}>{risk.level}</span>
                    <div><p className="font-semibold text-slate-950">{risk.title}</p><p className="mt-1 text-sm leading-6 text-slate-600">{risk.body}</p></div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="border-b border-slate-200 p-7 sm:p-9">
              <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-[#1a4e94]">Suggested plan</h3>
              <ol className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {result.phases.map((phase, index) => (
                  <li key={phase.title} className="rounded-2xl bg-[#f3f7fd] p-5">
                    <p className="text-xs font-bold text-[#1a4e94]">PHASE {index + 1}</p>
                    <p className="mt-1.5 font-semibold text-slate-950">{phase.title}</p>
                    <p className="mt-1.5 text-sm leading-6 text-slate-600">{phase.body}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section className="border-b border-slate-200 p-7 sm:p-9">
              <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-[#1a4e94]">Prepare before anyone quotes it</h3>
              <ul className="mt-3 list-disc space-y-2 pl-5 leading-7 text-slate-700">
                {result.prepare.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <p className="mt-4 text-sm text-slate-600">Timelines and cost depend on things a form cannot see. See <Link to="/blog/how-long-does-a-sigma-migration-take" className="text-[#1a4e94] underline underline-offset-4">how long a Sigma migration takes</Link> and <Link to="/blog/power-bi-tableau-to-sigma-migration-cost" className="text-[#1a4e94] underline underline-offset-4">what drives the cost</Link>.</p>
            </section>

            <section className="flex flex-wrap items-center justify-between gap-5 bg-[#0a1628] p-7 text-white sm:p-9" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
              <div className="min-w-0 flex-[1_1_380px]">
                <p className="text-xl font-semibold">Get a free migration analysis</p>
                <p className="mt-1.5 text-sm leading-6 text-slate-300">Walk through this readout with Linus. Bring a few {meta.reportNoun}, and leave with a pilot pick and a scoped plan. {SITE_URL.replace('https://', '')}</p>
              </div>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[46px] items-center rounded-xl bg-white px-5 font-semibold text-[#0a1628] hover:bg-slate-100">Book a free analysis</a>
            </section>
          </article>
        </div>
      )}

      <section className="no-print mt-14 grid gap-6 md:grid-cols-3">
        <Link to={page.translator} className="rounded-3xl border border-slate-200 p-6 hover:border-blue-200 hover:shadow-lg">
          <p className="font-semibold text-slate-950">{source === 'tableau' ? 'Tableau' : 'DAX'} to Sigma formula translator</p>
          <p className="mt-2 text-sm leading-6 text-slate-600">Translate the calculations this readout flags, one at a time.</p>
        </Link>
        <Link to={page.plan} className="rounded-3xl border border-slate-200 p-6 hover:border-blue-200 hover:shadow-lg">
          <p className="font-semibold text-slate-950">How to plan the migration</p>
          <p className="mt-2 text-sm leading-6 text-slate-600">The {meta.label}-specific planning guide: sources, calculations, and security.</p>
        </Link>
        <Link to={page.service} className="rounded-3xl border border-slate-200 p-6 hover:border-blue-200 hover:shadow-lg">
          <p className="font-semibold text-slate-950">{meta.label} to Sigma migration service</p>
          <p className="mt-2 text-sm leading-6 text-slate-600">How Greyfield runs the move, from assessment to cutover.</p>
        </Link>
      </section>
    </BlogLayout>
  );
}
